import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import { chain, pickBy } from 'lodash';
import {
  Intent,
  Button,
  FormGroup,
  InputGroup,
  FileInput,
  ButtonProps,
  Callout,
} from '@blueprintjs/core';
import { DatasetStatus } from '@retro-ui/api-contract';

import {
  createUpload,
  resumeUpload,
} from '@f/services/starting-material/starting-material.actions';
import { StartingMaterialReducer } from '@f/services/starting-material/starting-material.types';
import { formatBytes } from '@f/utils/text';
import { useConfig } from '@f/hooks';

import cls from './UploadForm.module.scss';

const ALLOWED_EXTENSIONS = ['.csv'];

interface UploadFormProps {
  children?: React.ReactNode;
  className?: string;
  large?: boolean;
  maxUploadSize?: number;
  existingDatasetId?: string;
  existingDatasetSize?: number;
  submitButtonProps: ButtonProps;
  onSubmit?: (file: File) => unknown;
}

export default function UploadForm(props: UploadFormProps) {
  const {
    children,
    className,
    large,
    existingDatasetId,
    existingDatasetSize,
    submitButtonProps,
    onSubmit = () => {},
  } = props;

  const config = useConfig();
  const { datasetSizeLimitPerUpload } = config.startingMaterial;
  const formattedMaxDatasetUploadSize = formatBytes(datasetSizeLimitPerUpload, { unit: 'GB' });

  const privateDatasetNames = useSelector((state: any) => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return chain(
      pickBy(
        startingMaterial.dataset.data,
        dataset =>
          dataset.isPrivate &&
          ![DatasetStatus.REMOVED, DatasetStatus.CANCELED].includes(dataset.status)
      )
    )
      .values()
      .value()
      .map(item => item.name);
  });

  const [name, setName] = useState<string>('');
  const [file, setFile] = useState<File>();

  const existingDatasetItem = useSelector((state: any) => {
    if (typeof existingDatasetId === 'undefined') {
      return null;
    }

    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return startingMaterial.dataset.data[existingDatasetId];
  });

  const maxUploadSize = props.maxUploadSize || datasetSizeLimitPerUpload;

  const isUnnamed = !(existingDatasetItem?.name ?? name);
  const isNameDuplicate = privateDatasetNames.includes(name);
  const isFileSizeExceeded = Boolean(file && file?.size > maxUploadSize);
  const isResumedFileInvalid = Boolean(
    file && existingDatasetSize && file.size !== existingDatasetSize
  );

  const dispatch = useDispatch();

  const onNameChange = (event: React.FormEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;
    const newName = target.value;
    setName(newName);
  };

  const onFileChange = (event: React.FormEvent<HTMLInputElement>): void => {
    if (!event.currentTarget.files) {
      return;
    }
    const file = event.currentTarget.files[0];
    setFile(file);
  };

  const clearFile = (event: React.MouseEvent<unknown>) => {
    if (event.target instanceof HTMLInputElement) {
      event.target.value = '';
    }
    setFile(undefined);
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (isNameDuplicate || isFileSizeExceeded) {
      return;
    }

    if (!existingDatasetItem) {
      dispatch(createUpload(name, file as File));
    } else {
      dispatch(resumeUpload(existingDatasetItem, file as File));
    }

    onSubmit(file as File);
  };

  return (
    <form className={cx(cls.form, className)} onSubmit={onFormSubmit}>
      {children}
      {!existingDatasetItem && (
        <FormGroup
          className={cls.formGroup}
          label="Name"
          labelInfo="(required)"
          helperText="A unique name for the starting material dataset"
        >
          <InputGroup
            large={large}
            placeholder="Enter name..."
            value={name}
            onChange={onNameChange}
            intent={isNameDuplicate ? 'danger' : 'none'}
          />
          {isNameDuplicate && (
            <Callout intent={Intent.DANGER} className={cls.error}>
              This name is already taken. Please choose a different name.
            </Callout>
          )}
        </FormGroup>
      )}
      <FormGroup
        className={cls.formGroup}
        label="File to upload"
        labelInfo="(required)"
        helperText={
          <>
            Allowed extensions: {ALLOWED_EXTENSIONS.join(', ')}
            <br />
            Maximum file size: {formattedMaxDatasetUploadSize}
          </>
        }
      >
        <FileInput
          large={large}
          hasSelection={Boolean(file)}
          inputProps={{
            accept: ALLOWED_EXTENSIONS.join(','),
          }}
          text={file?.name}
          buttonText="Browse"
          onInputChange={onFileChange}
          onClick={clearFile}
          className={cls.fileInput}
        />
        {isFileSizeExceeded && (
          <Callout intent={Intent.DANGER} className={cls.error}>
            File size must not exceed {formattedMaxDatasetUploadSize}.
          </Callout>
        )}
        {isResumedFileInvalid && (
          <Callout intent={Intent.DANGER} className={cls.error}>
            Resumed upload file must be the same as the original file.
          </Callout>
        )}
      </FormGroup>
      <Button
        className={cx(cls.submitButton, submitButtonProps.className)}
        large={large}
        outlined
        type="submit"
        disabled={
          !file || isUnnamed || isNameDuplicate || isFileSizeExceeded || isResumedFileInvalid
        }
        intent={Intent.PRIMARY}
        text="Upload"
        {...submitButtonProps}
        icon="cloud-upload"
        data-test="upload-starting-material-dataset-button"
      />
    </form>
  );
}
